import type { ToolLifecycle } from '../tools/tool-lifecycle'
/**
 * gemini-native-stream.ts
 * Gemini 原生 generateContent 流式解析。
 *
 * OpenAI 兼容模式拿不到 thought；必须走原生 streamGenerateContent +
 * generationConfig.thinkingConfig.includeThoughts=true，才能拿到思考内容。
 * 实测：返回 candidates[0].content.parts，part.thought===true 为思考，否则正文。
 */
import type { StreamEvent } from './llm'

import { duplicateToolResult, getToolLifecycleByName, ignoredAfterTerminalResult, normalizeToolResult, ToolExecutionLedger, ToolInputError } from '../tools/tool-lifecycle'
import { moveNonStableSystemLayersToLatestUser } from '../utils/prompt-cache-layout'
import { parseDataUrl } from './chat-helpers'
import { logCacheUsage } from './llm-cache-diagnostics'
import { getDisallowedToolCallResult, shouldUsePlainTextFallback } from './structured-output'

/** Gemini 原生 content：role 为 'user' 或 'model'，parts 为未知结构数组。 */
export interface GeminiContent { role: 'user' | 'model', parts: unknown[] }

/** 把单个 Gemini part 分类为 reasoning（思考）或 text（正文）。 */
export function parseGeminiPart(
  part: { thought?: boolean, text?: string },
): { kind: 'text' | 'reasoning', text: string } {
  return {
    kind: part.thought === true ? 'reasoning' : 'text',
    text: part.text ?? '',
  }
}

/** OpenAI 风格消息（content 为字符串或 part 数组）。 */
interface OpenAIStyleMessage { role: string, content: unknown, metadata?: unknown }

/**
 * OpenAI 风格 messages → Gemini 原生 contents。
 * 照 Operit buildContentsAndCountTokens 非工具分支 + buildPartsArray：
 * - system 消息滤出，用 \n\n 拼进 systemInstruction。
 * - assistant → role:'model'，其余 → 'user'。
 * - content 字符串 → [{ text }]；数组 → 逐 part（text / 图片 inline_data）。
 */
export function messagesToGeminiContents(
  messages: OpenAIStyleMessage[],
): { contents: GeminiContent[], systemInstruction?: GeminiContent } {
  const normalizedMessages = moveNonStableSystemLayersToLatestUser(messages)
  const systemText = normalizedMessages
    .filter(m => m.role === 'system')
    .map(m => (typeof m.content === 'string' ? m.content : ''))
    .filter(t => t.length > 0)
    .join('\n\n')

  const contents: GeminiContent[] = []
  for (const m of normalizedMessages) {
    if (m.role === 'system')
      continue
    const role: 'user' | 'model' = m.role === 'assistant' ? 'model' : 'user'
    contents.push({ role, parts: contentToGeminiParts(m.content) })
  }

  const systemInstruction: GeminiContent | undefined = systemText.length > 0
    ? { role: 'user', parts: [{ text: systemText }] }
    : undefined

  return { contents, systemInstruction }
}

/** 把单条消息的 content（字符串或 part 数组）转为 Gemini parts。 */
function contentToGeminiParts(content: unknown): unknown[] {
  if (typeof content === 'string')
    return [{ text: content }]
  if (!Array.isArray(content))
    return []
  const parts: unknown[] = []
  for (const part of content) {
    if (!part || typeof part !== 'object')
      continue
    const p = part as { type?: string, text?: string, image_url?: { url?: string } }
    if (p.type === 'text') {
      parts.push({ text: p.text ?? '' })
    }
    else if (p.type === 'image_url') {
      const parsed = p.image_url?.url ? parseDataUrl(p.image_url.url) : null
      if (parsed)
        parts.push({ inline_data: { mime_type: parsed.mediaType, data: parsed.data } })
      // 解析失败的图片跳过
    }
  }
  return parts
}

/** OpenAI 风格 function tool。 */
interface OpenAIStyleTool { type?: string, function?: { name?: string, description?: string, parameters?: unknown } }

/**
 * OpenAI 风格 tools → Gemini functionDeclarations。
 * 空/无工具返回 null。
 */
export function toolsToGeminiDeclarations(
  tools: OpenAIStyleTool[],
): { functionDeclarations: unknown[] } | null {
  if (!Array.isArray(tools) || tools.length === 0)
    return null
  const declarations = tools
    .filter(t => t.function?.name)
    .map(t => ({
      name: t.function!.name,
      description: t.function!.description,
      parameters: t.function!.parameters,
    }))
  if (declarations.length === 0)
    return null
  return { functionDeclarations: declarations }
}

/** 单次 Gemini 流的聚合结果（与 CompatStreamResult 对称，供工具循环消费）。 */
export interface GeminiStreamResult {
  textContent: string
  reasoningContent: string
  /** Gemini functionCall 无 id，仅有 name + args；id 是本地为 UI 事件配对生成的唯一标识（${name}#${index}）。 */
  toolCalls: Array<{ id: string, name: string, args: unknown }>
}

/**
 * 单次 POST streamGenerateContent，解析 SSE：
 * - part.thought===true → reasoning-delta（累加 reasoningContent）
 * - part.text           → text-delta（累加 textContent）
 * - part.functionCall   → 收集为工具调用（并发 tool-call 事件）
 * 与 executeOpenAICompatStream 对称：手搓请求、复用注入的 fetch、可离线单测。
 */
export async function executeGeminiStream(
  url: string,
  apiKey: string,
  body: Record<string, unknown>,
  onEvent: (e: StreamEvent) => void | Promise<void>,
  fetchImpl: typeof fetch,
  abortSignal?: AbortSignal,
  extraHeaders?: Record<string, string>,
  modelName?: string,
): Promise<GeminiStreamResult> {
  const model = modelName ?? 'unknown'
  const serializedBody = JSON.stringify(body)
  let res: Response
  try {
    res = await fetchImpl(url, {
      method: 'POST',
      // extraHeaders 先展开，再固定注入 content-type / x-goog-api-key，确保鉴权头不被用户 header 覆盖。
      headers: { ...extraHeaders, 'content-type': 'application/json', 'x-goog-api-key': apiKey },
      body: serializedBody,
      ...(abortSignal ? { signal: abortSignal } : {}),
    })
  }
  catch (error) {
    logCacheUsage('Gemini', model)
    throw error
  }
  if (!res.ok || !res.body) {
    logCacheUsage('Gemini', model)
    const err = new Error(`Gemini native HTTP ${res.status}`) as Error & { status?: number }
    err.status = res.status
    throw err
  }
  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buf = ''
  let textContent = ''
  let reasoningContent = ''
  const toolCalls: Array<{ id: string, name: string, args: unknown }> = []
  let lastUsage: { prompt?: number, cached?: number } | null = null

  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done)
        break
      const decoded = decoder.decode(value, { stream: true })
      buf += decoded
      const lines = buf.split('\n')
      buf = lines.pop() ?? ''
      for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed.startsWith('data:'))
          continue
        const json = trimmed.slice(5).trim()
        if (!json || json === '[DONE]')
          continue
        let parsed: {
          candidates?: Array<{
            content?: { parts?: Array<{ thought?: boolean, text?: string, functionCall?: { name?: string, args?: unknown } }> }
          }>
          usageMetadata?: {
            promptTokenCount?: number
            cachedContentTokenCount?: number
          }
        }
        try {
          parsed = JSON.parse(json)
        }
        catch {
          continue
        }
        const parts = parsed?.candidates?.[0]?.content?.parts ?? []
        for (const p of parts) {
          if (p.functionCall?.name) {
            const name = p.functionCall.name
            const args = p.functionCall.args ?? {}
            const id = `${name}#${toolCalls.length}`
            toolCalls.push({ id, name, args })
            await onEvent({ type: 'tool-call-streaming-start', toolCallId: id, toolName: name })
            await onEvent({ type: 'tool-call', toolCallId: id, toolName: name, args } as StreamEvent)
            continue
          }
          const { kind, text } = parseGeminiPart(p)
          if (text.length === 0)
            continue
          if (kind === 'reasoning') {
            reasoningContent += text
            await onEvent({ type: 'reasoning-delta', text })
          }
          else {
            textContent += text
            await onEvent({ type: 'text-delta', text })
          }
        }
        // 跟踪最后一帧 usage（每个 chunk 都带，取最后一个最准确）
        if (parsed?.usageMetadata) {
          const u = parsed.usageMetadata
          lastUsage = { prompt: u.promptTokenCount, cached: u.cachedContentTokenCount }
        }
      }
    }
  }
  finally {
    // 无论正常结束还是异常，都释放 reader 锁，确保底层连接可关闭
    reader.cancel().catch(() => {})
  }
  logCacheUsage('Gemini', model, lastUsage
    ? {
        totalInputTokens: lastUsage.prompt,
        cachedInputTokens: lastUsage.cached,
      }
    : undefined)

  return { textContent, reasoningContent, toolCalls }
}

/** runGeminiToolLoop 的依赖（与 CompatLoopDeps 对称）。 */
export interface GeminiLoopDeps {
  streamOnce: (contents: GeminiContent[]) => Promise<GeminiStreamResult>
  toolExecutors: Map<string, (input: unknown, opts: unknown) => Promise<unknown>>
  terminalToolNames: Set<string>
  maxSteps: number
  onEvent: (e: StreamEvent) => void | Promise<void>
  ledger?: ToolExecutionLedger
  onToolBudgetExceeded?: () => void
  actionToolNames?: ReadonlySet<string>
  lifecycleByName?: ReadonlyMap<string, ToolLifecycle>
  requireToolCall?: boolean
  allowedToolNames?: ReadonlySet<string>
}

/**
 * Gemini 多步工具循环，复刻 runCompatToolLoop 的终止语义，但用 Gemini 消息格式：
 * - 模型轮：{ role:'model', parts:[{text?}, {functionCall:{name,args}}...] }
 * - 结果轮：{ role:'user',  parts:[{functionResponse:{name, response:{result}}}...] }
 * 终止工具（terminalToolNames，如 send_messages）执行后不回送 functionResponse 且结束本轮。
 */
export async function runGeminiToolLoop(
  initialContents: GeminiContent[],
  deps: GeminiLoopDeps,
): Promise<void> {
  const { streamOnce, toolExecutors, terminalToolNames, maxSteps, onEvent } = deps
  let contents = [...initialContents]
  const ledger = deps.ledger ?? new ToolExecutionLedger(maxSteps)

  for (let step = 0; step < maxSteps; step++) {
    const result = await streamOnce(contents)
    if (deps.requireToolCall && result.textContent.trim())
      await onEvent({ type: 'text-delta', text: result.textContent })

    // 无工具调用：正常收尾
    if (result.toolCalls.length === 0) {
      await onEvent({ type: 'finish', finishReason: 'stop' } as StreamEvent)
      return
    }

    if (shouldUsePlainTextFallback(
      result.toolCalls.map(tool => tool.name),
      result.textContent,
      deps.requireToolCall,
      deps.allowedToolNames,
    )) {
      await onEvent({ type: 'finish', finishReason: 'stop' } as StreamEvent)
      return
    }

    // 工具调用时忽略同轮附加文本，避免把确认语送回下一轮上下文。
    const modelParts: unknown[] = []
    for (const tc of result.toolCalls)
      modelParts.push({ functionCall: { name: tc.name, args: tc.args } })

    // 执行工具；终止工具不回送 functionResponse
    const responseParts: unknown[] = []
    let hitTerminal = false
    let hitBudget = false
    for (const tc of result.toolCalls) {
      if (hitTerminal) {
        await onEvent({ type: 'tool-result', toolCallId: tc.id, result: ignoredAfterTerminalResult() })
        continue
      }
      if (deps.allowedToolNames && !deps.allowedToolNames.has(tc.name)) {
        responseParts.push({ functionResponse: { name: tc.name, response: { result: getDisallowedToolCallResult(tc.name, deps.allowedToolNames) } } })
        continue
      }
      const lifecycle = deps.actionToolNames?.has(tc.name) ? 'action' : getToolLifecycleByName(tc.name, deps.lifecycleByName, terminalToolNames)
      const claim = ledger.claim(tc.id, tc.name, tc.args)
      if (!claim.accepted) {
        const content = duplicateToolResult(claim)
        await onEvent({ type: 'tool-result', toolCallId: tc.id, result: content })
        if (claim.reason === 'budget') {
          ledger.notifyBudgetOnce(deps.onToolBudgetExceeded)
          hitBudget = true
          break
        }
        responseParts.push({ functionResponse: { name: tc.name, response: { result: content } } })
        continue
      }
      const executor = toolExecutors.get(tc.name)
      let content = ''
      let executionError: unknown
      if (executor) {
        try {
          const r = await executor(tc.args, {})
          content = normalizeToolResult(r, tc.name, lifecycle)
        }
        catch (e) {
          executionError = e
          content = e instanceof Error ? e.message : String(e)
        }
      }
      else {
        if (lifecycle === 'terminal')
          throw new Error(`Terminal tool "${tc.name}" is not registered`)
        content = `Tool "${tc.name}" not found`
      }
      await onEvent({ type: 'tool-result', toolCallId: tc.id, result: content })

      if (lifecycle === 'terminal') {
        if (executionError && !ledger.allowInputCorrection(executionError, tc.id, tc.name, tc.args)) {
          if (executionError instanceof ToolInputError) {
            await onEvent({ type: 'finish', finishReason: 'stop' } as StreamEvent)
            return
          }
          throw executionError
        }
        if (!executionError) {
          hitTerminal = true
          continue
        }
      }
      if (ledger.budgetReached) {
        ledger.notifyBudgetOnce(deps.onToolBudgetExceeded)
        hitBudget = true
        break
      }
      responseParts.push({ functionResponse: { name: tc.name, response: { result: content } } })
    }

    if (hitTerminal || hitBudget || responseParts.length === 0) {
      await onEvent({ type: 'finish', finishReason: 'stop' } as StreamEvent)
      return
    }

    contents = [
      ...contents,
      { role: 'model', parts: modelParts },
      { role: 'user', parts: responseParts },
    ]
  }

  await onEvent({ type: 'finish', finishReason: 'stop' } as StreamEvent)
}
